import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";

import { Position } from "~/entity/Employee/model/schema";
import { getDataPositions } from "~/pages/StaffEmployeePersonal/GetStaffPersonal/getDataExpectationsPersonal";
import { ISelectOption } from "~/shared/ui/Fields/types";

const PAGE_SIZE = 25;

export const positionToSelectOption = (position: Position): ISelectOption => ({
  value: position.instanceId,
  label: `${position.code}. ${position.name}`,
});

const mergeUniqueOptions = (
  prev: ISelectOption[],
  next: ISelectOption[]
): ISelectOption[] => [
  ...prev,
  ...next.filter(
    (item) => !prev.some((el) => String(el.value) === String(item.value))
  ),
];

/**
 * Опции должностей из REST GET /employee/positions с пагинацией по скроллу,
 * серверным поиском и догрузкой опций по instanceId (ensureOptionsByIds)
 * для предзаполнения селекта, когда известны только id.
 */
export const useGetPositionsOptions = (date?: string) => {
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const totalRef = useRef(0);
  const pageRef = useRef(0);
  const searchRef = useRef<string | undefined>(undefined);

  const load = async ({ append = false }: { append?: boolean } = {}) => {
    setLoading(true);
    try {
      const data = await getDataPositions(date, ["active"], {
        page: pageRef.current,
        count: PAGE_SIZE,
        searchText: searchRef.current,
      });
      const items: ISelectOption[] = (data?.positions || []).map(
        positionToSelectOption
      );
      totalRef.current = data?.total ?? items.length;
      setOptions((prev) =>
        append ? mergeUniqueOptions(prev, items) : items
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    pageRef.current = 0;
    searchRef.current = undefined;
    load();
  }, [date]);

  const onScrollBottom = () => {
    if (loading || options.length >= totalRef.current) {
      return;
    }
    pageRef.current += 1;
    load({ append: true });
  };

  const searchText = debounce((text: string) => {
    searchRef.current = text || undefined;
    pageRef.current = 0;
    load();
  }, 300);

  /**
   * Догружает в опции должности по instanceId, которых еще нет в списке
   * (без фильтра по статусу, чтобы найти и неактивные).
   * Каждый id запрашивается не более одного раза.
   */
  const ensuredIdsRef = useRef<Set<string>>(new Set());

  const ensureOptionsByIds = async (ids: (string | number)[]) => {
    const missing = ids.filter((id) => {
      const key = String(id);
      return (
        !ensuredIdsRef.current.has(key) &&
        !options.some((el) => String(el.value) === key)
      );
    });
    if (!missing.length) {
      return;
    }
    missing.forEach((id) => ensuredIdsRef.current.add(String(id)));
    const data = await getDataPositions(date, undefined, {
      instanceIds: missing,
      count: missing.length,
    });
    const items: ISelectOption[] = (data?.positions || []).map(
      positionToSelectOption
    );
    setOptions((prev) => mergeUniqueOptions(prev, items));
  };

  return {
    options,
    loading,
    onScrollBottom,
    searchText,
    ensureOptionsByIds,
  };
};
